'use client'

import { useState } from 'react'
import styles from './FeedSidebar.module.scss'
import { Chip, Stack, Snackbar, Divider } from '@mui/material'
import { ITag } from '@/interfaces/ITag'
import { IUser } from '@/interfaces/IUser'
import NextLink from 'next/link'
import FollowSuggestionRow from '@/components/display/FollowSuggestionRow'
import { PATHWAY } from '@/modules/httpService'
import { useTranslations } from 'next-intl'

function FeedSidebar ({ tags, accounts, isLoggedIn }: { isLoggedIn: boolean, tags: ITag[], accounts: IUser[] }) {
  const [followUsers, setFollowUsers] = useState<IUser[]>([])
  const [updatedFollowers, setUpdatedFollowers] = useState<IUser[]>([])
  const [statusMessage, toggleMessage] = useState('')

  async function handleFollow (user: IUser) {
    try {
      setFollowUsers([...followUsers, user])

      // Call the API to follow the user
      await PATHWAY.post(`/users/${user?.id}/followers`, {})

      setUpdatedFollowers([...updatedFollowers, user])

      // remove the user from the followUsers array
      const newFollowUsers = followUsers.filter(f => f.id !== user.id)
      setFollowUsers(newFollowUsers)

      toggleMessage(`You are now following ${user.username}`)
    } catch (err: unknown) {
      console.error(err)
      const newFollowUsers = followUsers.filter(f => f.id !== user.id)
      setFollowUsers(newFollowUsers)
      toggleMessage('An error occurred while trying to follow the user')
    }
  }

  const t = useTranslations('FeedSidebar')
  const m = useTranslations('MainFooter')

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.innerWrapper}>
          <h4>{t('recommended-topics')}</h4>
          <Stack direction="column" spacing={2} useFlexGap style={{ flexWrap: 'wrap', marginBottom: '1rem' }}>
            {tags.map((tag, i) => {
              return (
                <NextLink
                  key={i}
                  href={'?topic=' + tag.id}
                >
                  <Chip
                    className={styles.chip}
                    label={tag.name}
                  />
                </NextLink>
              )
            })}
            <div className={styles.linkWrapper}>
              <NextLink href='/topics' className={styles.link}>{t('see-full-list')}</NextLink>
            </div>
          </Stack>
          {isLoggedIn &&
            <>
              <h4 className={styles.whoToFollowTitle}>{t('suggested-accounts')}</h4>
              <Stack direction="row" spacing={3} useFlexGap style={{ flexWrap: 'wrap', marginBottom: '1rem' }}>
                {accounts.map((account, i) => {
                  return (
                    <FollowSuggestionRow
                      key={i}
                      user={account}
                      isSubscribing={followUsers.filter(f => f.id === account.id).length > 0}
                      isSubscribed={updatedFollowers.filter(f => f.id === account.id).length > 0}
                      handleFollow={(u: IUser) => handleFollow(u)}
                    />
                  )
                })}
                {accounts.length === 0 &&
                  <div className={styles.noAccounts}>
                    {t('no-more-suggestions')}
                  </div>
                }
                <div className={styles.linkWrapper}>
                  <NextLink href='/accounts' className={styles.link}>{t('see-more-suggestions')}</NextLink>
                </div>
              </Stack>
            </>
          }
          <>
            <Divider />
            <div className={styles.policies}>
              {/* <NextLink style={{ marginRight: 5, fontSize: '0.8rem' }} href={'/'}>Home</NextLink>
              <NextLink style={{ marginRight: 5, fontSize: '0.8rem' }} href={'/about'}>About</NextLink> */}
              <NextLink style={{ width: '100%', fontSize: '0.8rem' }} href={'/policies/privacy-policy'}>{t('privacy-policy')}</NextLink>
              <NextLink style={{ width: '100%', marginRight: 5, fontSize: '0.8rem' }} href={'/policies/terms-of-service'}>{t('terms-of-service')}</NextLink>
              {/* <NextLink style={{ width: '100%', fontSize: '0.8rem' }} href={'/policies/community-guidelines'}>{t('community-guidelines')}</NextLink> */}
              <NextLink style={{ width: '100%', fontSize: '0.8rem' }} href={'/policies/statement-of-faith'}>{t('statement-of-faith')}</NextLink>
              <NextLink style={{ width: '100%', fontSize: '0.8rem' }} href={'/policies/community-guidelines'}>{t('community-guidelines')}</NextLink>
              <NextLink style={{ width: '100%', fontSize: '0.8rem' }} href={'/contact'}>{t('contact')}</NextLink>
              <NextLink style={{ width: '100%', fontSize: '0.8rem' }} href={'/donate'}>{m('donate')}</NextLink>
            </div>
          </>
        </div>
      </div>
      <Snackbar
        open={statusMessage !== ''}
        autoHideDuration={6000}
        onClose={() => toggleMessage('')}
        message={statusMessage}
      />
    </>
  )
}

export default FeedSidebar
